import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCamera, FiUsers, FiDollarSign, FiTrendingUp, FiCalendar, FiImage, FiStar, FiEye } = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();

  const stats = [
    {
      name: 'Total Photos',
      value: '2,847',
      change: '+12%',
      changeType: 'increase',
      icon: FiImage,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'Active Galleries',
      value: '23',
      change: '+3',
      changeType: 'increase',
      icon: FiCamera,
      color: 'from-purple-500 to-pink-500'
    },
    {
      name: 'Total Views',
      value: '12.5K',
      change: '+18%',
      changeType: 'increase',
      icon: FiEye,
      color: 'from-green-500 to-emerald-500'
    },
    {
      name: 'Revenue',
      value: '$3,247',
      change: '+25%',
      changeType: 'increase',
      icon: FiDollarSign,
      color: 'from-orange-500 to-red-500'
    }
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'gallery_created',
      title: 'New gallery "Summer Wedding" created',
      time: '2 hours ago',
      icon: FiCamera
    },
    {
      id: 2,
      type: 'booking_received',
      title: 'New booking request from Sarah Johnson',
      time: '4 hours ago',
      icon: FiCalendar
    },
    {
      id: 3,
      type: 'sale_completed',
      title: 'Photo package sold for $250',
      time: '6 hours ago',
      icon: FiDollarSign
    },
    {
      id: 4,
      type: 'review_received',
      title: 'New 5-star review received',
      time: '1 day ago',
      icon: FiStar
    }
  ];

  const upcomingBookings = [
    {
      id: 1,
      client: 'Emily Davis',
      type: 'Portrait Session',
      date: '2024-01-15',
      time: '10:00 AM',
      status: 'confirmed'
    },
    {
      id: 2,
      client: 'Mike & Jessica',
      type: 'Engagement Shoot',
      date: '2024-01-18',
      time: '2:00 PM',
      status: 'pending'
    },
    {
      id: 3,
      client: 'Anderson Family',
      type: 'Family Photos',
      date: '2024-01-20',
      time: '11:00 AM',
      status: 'confirmed'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Welcome back, {user?.name}! 👋
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Here's what's happening with your photography business today.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
              className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.name}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {stat.value}
                  </p>
                  <p className={`text-sm mt-1 ${
                    stat.changeType === 'increase' 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {stat.change} from last month
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                  <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-2 bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Recent Activity
            </h2>
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <SafeIcon icon={activity.icon} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {activity.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Upcoming Bookings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Upcoming Bookings
            </h2>
            <div className="space-y-4">
              {upcomingBookings.map((booking) => (
                <div key={booking.id} className="p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {booking.client}
                    </h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      booking.status === 'confirmed' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {booking.type}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    {booking.date} at {booking.time}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-8 text-white"
        >
          <h2 className="text-2xl font-bold mb-4">Quick Actions</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <button className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-left hover:bg-white/30 transition-colors">
              <SafeIcon icon={FiCamera} className="w-6 h-6 mb-2" />
              <h3 className="font-semibold">Create Gallery</h3>
              <p className="text-sm opacity-90">Upload and organize new photos</p>
            </button>
            <button className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-left hover:bg-white/30 transition-colors">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 mb-2" />
              <h3 className="font-semibold">Schedule Booking</h3>
              <p className="text-sm opacity-90">Set up a new photo session</p>
            </button>
            <button className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-left hover:bg-white/30 transition-colors">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 mb-2" />
              <h3 className="font-semibold">View Analytics</h3>
              <p className="text-sm opacity-90">Check your performance metrics</p>
            </button>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;